// Klub panel — Táblák (Figma OP_02, 02b, 03, 03b, 04): kezelés, foglalási naptár, foglalás statisztika.
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Icon } from '../icons';
import { fmtInt } from '../data';
import {
  Avatar, Badge, Button, Card, ConfirmModal, Empty, Field, Input, Modal, PageHeader, SectionTitle, Segmented, Select, Spinner, StatCard, Tabs, Toggle, useToast,
} from '../ui';
import {
  bookingPrice, bookingTitle, ClubBoard, ClubBooking, ClubInfo, DAY_NAMES, openWindow, useBookings, useBoards, useClub, usePeople,
} from './clubData';
import { BookingDrawer, hhmm, NewBookingModal } from './clubUi';

type Tab = 'manage' | 'calendar' | 'stats';

export default function ClubBoardsPage() {
  const { club } = useClub();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'manage';
  const boards = useBoards(club?.id);
  const bookings = useBookings(club?.id, 70, 60);
  const [range, setRange] = useState<'day' | 'week' | 'month'>('week');
  const [editing, setEditing] = useState<ClubBoard | 'new' | null>(null);
  const [adding, setAdding] = useState(false);

  if (!club || !boards || !bookings) return <Spinner />;

  const right = tab === 'manage' ? <Button icon="Plus" onClick={() => setEditing('new')}>Új tábla</Button>
    : tab === 'calendar' ? <Button icon="Plus" onClick={() => setAdding(true)}>Új foglalás</Button>
      : <Segmented value={range} onChange={setRange} options={[{ value: 'day', label: 'Napi' }, { value: 'week', label: 'Heti' }, { value: 'month', label: 'Havi' }]} />;

  return (
    <div>
      <PageHeader title="Táblák" sub={`${club.name} · ${boards.length} tábla`} right={right} />
      <Tabs<Tab> value={tab} onChange={(t) => setParams({ tab: t })} options={[
        { value: 'manage', label: 'Táblák kezelése' }, { value: 'calendar', label: 'Foglalási naptár' }, { value: 'stats', label: 'Foglalás statisztika' },
      ]} />
      {tab === 'manage' && <ManageTab club={club} boards={boards} bookings={bookings} onEdit={setEditing} />}
      {tab === 'calendar' && <CalendarTab club={club} boards={boards} bookings={bookings} />}
      {tab === 'stats' && <StatsTab club={club} boards={boards} bookings={bookings} range={range} />}
      {editing && <BoardModal club={club} boards={boards} board={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {adding && <NewBookingModal club={club} boards={boards} bookings={bookings} onClose={() => setAdding(false)} />}
    </div>
  );
}

/* ---------- Táblák kezelése (OP_02) ---------- */
function ManageTab({ club, boards, bookings, onEdit }: { club: ClubInfo; boards: ClubBoard[]; bookings: ClubBooking[]; onEdit: (b: ClubBoard) => void }) {
  const toast = useToast();
  const now = new Date();
  const busy = new Set(bookings.filter((b) => b.state !== 'cancelled' && b.start <= now && now < b.end).map((b) => b.board));
  const [del, setDel] = useState<ClubBoard | null>(null);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [bulk, setBulk] = useState('all');

  const setState = async (b: ClubBoard, state: 'free' | 'closed') => {
    try {
      await updateDoc(doc(db, 'clubs', club.id, 'boards', b.id), state === 'free'
        ? { state: 'free', closedUntil: null, closedReason: '', manualReopen: false, maintReason: null }
        : { state: 'closed', closedUntil: null, closedReason: 'Lezárva a klub panelről', manualReopen: true });
    } catch { toast('A módosítás nem sikerült.', 'red'); }
  };
  const applyBulk = async (state: 'free' | 'closed') => {
    const targets = bulk === 'all' ? boards : boards.filter((b) => b.id === bulk);
    await Promise.all(targets.map((b) => setState(b, state)));
    toast(state === 'closed' ? 'Táblák lezárva.' : 'Táblák megnyitva.');
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
      <div className="flex flex-col gap-2.5">
        {boards.length === 0 && <Card><Empty>Még nincs tábla. Az „Új tábla” gombbal vehetsz fel.</Empty></Card>}
        {boards.map((b) => {
          const st = b.state === 'maintenance' ? { t: 'Karbantartás', tone: 'gold' as const, c: '#d9a514' }
            : b.state === 'closed' ? { t: 'Lezárva', tone: 'gray' as const, c: '#8a8f99' }
              : busy.has(b.order) ? { t: 'Foglalt', tone: 'cyan' as const, c: '#00AFF2' } : { t: 'Szabad', tone: 'green' as const, c: '#2bc48a' };
          return (
            <div key={b.id} className="flex items-center gap-3 p-3 rounded-2xl bg-[#15191E] border border-[#31383D] flex-wrap">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl text-[16px] font-bold" style={{ background: `${st.c}24`, color: st.c }}>{b.order}</span>
              <div className="flex-1 min-w-[180px]">
                <div className="text-[14px] font-semibold text-white">{b.name}</div>
                <div className="text-[12px] text-[#8a8f99]">{[b.boardType || club.boardType, b.location, `${fmtInt(b.priceBase)} Ft / ${b.priceMinutes} perc`].join(' · ')}</div>
              </div>
              <Badge tone={st.tone}>{st.t}</Badge>
              <span className="text-[12px] text-[#8a8f99] ml-2">Foglalható</span>
              <Toggle checked={b.state === 'free'} onChange={(v) => setState(b, v ? 'free' : 'closed')} />
              <button type="button" onClick={() => onEdit(b)} aria-label="Szerkesztés"
                className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:text-white"><Icon name="PencilSimpleLine" size={16} /></button>
              <button type="button" onClick={() => setDel(b)} aria-label="Törlés"
                className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#ef4444]"><Icon name="Trash" size={16} /></button>
            </div>
          );
        })}
      </div>

      <Card className="!p-5">
        <SectionTitle right={<button type="button" onClick={() => setHoursOpen(true)} aria-label="Nyitvatartás szerkesztése"
          className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-[#1d232a] border border-[#31383D] text-[#B8B8C3] hover:text-white"><Icon name="PencilSimpleLine" size={16} /></button>}>
          Nyitvatartási idők
        </SectionTitle>
        {DAY_NAMES.map((d, i) => (
          <div key={d} className="flex justify-between py-2 text-[13px]">
            <span className="text-[#B8B8C3]">{d}</span>
            <span className="font-semibold text-white">{club.hours[i] ? `${club.hours[i]!.from} – ${club.hours[i]!.to}` : 'Zárva'}</span>
          </div>
        ))}
        <div className="mt-3 pt-4 border-t border-[#31383D]">
          <h3 className="text-[13px] font-extrabold italic uppercase text-white mb-3">Tömeges művelet</h3>
          <Select value={bulk} onChange={setBulk} options={[{ value: 'all', label: 'Minden tábla' }, ...boards.map((b) => ({ value: b.id, label: b.name }))]} />
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button kind="ghost" icon="Lock" onClick={() => applyBulk('closed')}>Lezárás</Button>
            <Button onClick={() => applyBulk('free')}>Megnyitás</Button>
          </div>
        </div>
      </Card>

      {del && (
        <ConfirmModal title="Tábla törlése" danger confirmLabel="Törlés" onClose={() => setDel(null)}
          text={<>A(z) <b className="text-white">{del.name}</b> archiválódik, többé nem foglalható. A korábbi foglalások megmaradnak.</>}
          onConfirm={async () => {
            try { await updateDoc(doc(db, 'clubs', club.id, 'boards', del.id), { state: 'archived', archivedAt: serverTimestamp() }); toast('Tábla törölve.'); } catch { toast('A törlés nem sikerült.', 'red'); }
            setDel(null);
          }} />
      )}
      {hoursOpen && <HoursModal club={club} onClose={() => setHoursOpen(false)} />}
    </div>
  );
}

function HoursModal({ club, onClose }: { club: ClubInfo; onClose: () => void }) {
  const toast = useToast();
  const [h, setH] = useState(club.hours.map((x) => (x ? { ...x } : null)));
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Nyitvatartás szerkesztése" onClose={onClose} width={560}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={busy} onClick={async () => {
        setBusy(true);
        try { await updateDoc(doc(db, 'clubs', club.id), { 'schedule.hours': h }); toast('Nyitvatartás mentve.'); onClose(); } catch { toast('A mentés nem sikerült.', 'red'); }
        setBusy(false);
      }}>Mentés</Button></>}>
      <div className="flex flex-col gap-2.5">
        {DAY_NAMES.map((d, i) => {
          const v = h[i];
          const set = (nv: typeof v) => setH((a) => a.map((x, j) => (j === i ? nv : x)));
          return (
            <div key={d} className="grid grid-cols-[100px_1fr_auto_1fr_auto] items-center gap-3 min-h-[40px]">
              <span className="text-[13px] font-semibold text-white">{d}</span>
              {v ? (
                <>
                  <input type="time" value={v.from} onChange={(e) => set({ ...v, from: e.target.value })} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-[14px] text-white [color-scheme:dark]" />
                  <span className="text-[#8a8f99]">–</span>
                  <input type="time" value={v.to} onChange={(e) => set({ ...v, to: e.target.value })} className="h-[40px] px-3 rounded-xl bg-[#1d232a] border border-[#31383D] text-[14px] text-white [color-scheme:dark]" />
                </>
              ) : <span className="col-span-3 text-[13px] italic text-[#8a8f99]">Zárva</span>}
              <Toggle checked={Boolean(v)} onChange={(on) => set(on ? { from: '16:00', to: '23:00' } : null)} />
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

const LOCATIONS = ['Főterem', 'Különterem', 'Emelet', 'Terasz'];
const TYPES = ['Steel tip', 'Soft tip'];

function BoardModal({ club, boards, board, onClose }: { club: ClubInfo; boards: ClubBoard[]; board: ClubBoard | null; onClose: () => void }) {
  const toast = useToast();
  const nextOrder = Math.max(0, ...boards.map((b) => b.order)) + 1;
  const [order, setOrder] = useState(String(board?.order ?? nextOrder));
  const [name, setName] = useState(board && board.name !== `${board.order}. tábla` ? board.name : '');
  const [loc, setLoc] = useState(board?.location ?? 'Főterem');
  const [type, setType] = useState(board?.boardType || (TYPES.includes(club.boardType) ? club.boardType : 'Steel tip'));
  const [price, setPrice] = useState(String(board?.priceBase ?? 4500));
  const [instant, setInstant] = useState(board ? board.state === 'free' : true);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const o = Number(order);
    if (!o || o < 1) return setMsg('Adj meg egy érvényes táblaszámot.');
    if (boards.some((b) => b.order === o && b.id !== board?.id)) return setMsg('Ilyen számú tábla már van.');
    setBusy(true);
    const data = {
      order: o, name: name.trim() || `${o}. tábla`, location: loc, boardType: type,
      priceBase: Number(price.replace(/\s/g, '')) || 0, priceMinutes: board?.priceMinutes ?? 60,
    };
    try {
      if (board) {
        await updateDoc(doc(db, 'clubs', club.id, 'boards', board.id), {
          ...data, ...(instant !== (board.state === 'free') ? (instant ? { state: 'free', closedReason: '', manualReopen: false } : { state: 'closed', closedReason: 'Előkészítés alatt', manualReopen: true }) : {}),
        });
      } else {
        await addDoc(collection(db, 'clubs', club.id, 'boards'), {
          ...data, state: instant ? 'free' : 'closed', closedUntil: null, closedReason: instant ? '' : 'Előkészítés alatt',
          manualReopen: !instant, maintReason: null, durations: [60, 90, 120], stepMin: 30, customPrices: false, pricePeriods: [], priceDays: [],
          createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'clubs', club.id), { boardCount: boards.length + 1 }).catch(() => undefined);
      }
      toast(board ? 'Tábla mentve.' : 'Tábla hozzáadva.');
      onClose();
    } catch {
      setMsg('A mentés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={board ? 'Tábla szerkesztése' : 'Új tábla hozzáadása'} onClose={onClose} width={480}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button icon={board ? undefined : 'Plus'} disabled={busy} onClick={save}>{board ? 'Mentés' : 'Hozzáadás'}</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Tábla száma"><Input inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value.replace(/[^\d]/g, ''))} /></Field>
        <Field label="Név (opcionális)"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. VIP tábla" /></Field>
        <Field label="Helyszín a klubban"><Select value={loc} onChange={setLoc} options={(LOCATIONS.includes(loc) ? LOCATIONS : [loc, ...LOCATIONS]).map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Táblatípus"><Select value={type} onChange={setType} options={TYPES.map((x) => ({ value: x, label: x }))} /></Field>
        <Field label={`Ár (Ft / ${board?.priceMinutes ?? 60} perc)`} className="col-span-2">
          <Input inputMode="numeric" value={fmtInt(Number(price) || 0)} onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))} />
        </Field>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-[#1d232a]">
        <div>
          <div className="text-[14px] font-semibold text-white">Azonnal foglalható</div>
          <div className="text-[12px] text-[#8a8f99]">A tábla megjelenik a játékosok foglalási felületén</div>
        </div>
        <Toggle checked={instant} onChange={setInstant} />
      </div>
      {msg && <div className="mt-3 text-[13px] text-[#ef4444]" role="alert">{msg}</div>}
    </Modal>
  );
}

/* ---------- Foglalási naptár (OP_03 + 03b) ---------- */
const KIND_COLOR = { cp: '#00AFF2', friendly: '#2bc48a', manual: '#2bc48a', event: '#a855f7' } as const;

function CalendarTab({ club, boards, bookings }: { club: ClubInfo; boards: ClubBoard[]; bookings: ClubBooking[] }) {
  const [day, setDay] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [openId, setOpenId] = useState<string | null>(null);
  const who = usePeople(bookings.map((b) => b.createdBy));
  const w = openWindow(club.hours, day);
  const from = w?.from ?? new Date(day.getTime() + 14 * 36e5);
  const to = w?.to ?? new Date(day.getTime() + 24 * 36e5);
  const span = to.getTime() - from.getTime();
  const hours: Date[] = [];
  for (let t = Math.ceil(from.getTime() / 36e5) * 36e5; t < to.getTime(); t += 36e5) hours.push(new Date(t));
  const dayList = bookings.filter((b) => b.state !== 'cancelled' && b.end > from && b.start < to);
  const open = bookings.find((b) => b.id === openId) ?? null;
  const pct = (d: Date) => Math.max(0, Math.min(100, ((d.getTime() - from.getTime()) / span) * 100));
  const shift = (n: number) => setDay((d) => new Date(d.getTime() + n * 864e5));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <Button kind="ghost" icon="CaretLeft" onClick={() => shift(-1)}>Előző nap</Button>
        <div className="text-center">
          <div className="text-[15px] font-bold text-white">{day.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}</div>
          <div className="text-[12px] text-[#8a8f99]">{w ? `Nyitva: ${hhmm(w.from)} – ${hhmm(w.to)}` : 'Zárva'}</div>
        </div>
        <button type="button" onClick={() => shift(1)} className="inline-flex items-center gap-2 h-[37px] px-[18px] rounded-full bg-[#1d232a] border border-[#31383D] text-[14px] font-semibold text-white">
          Következő nap <Icon name="CaretRight" size={16} />
        </button>
      </div>

      <div className="bg-[#15191E] border border-[#31383D] rounded-2xl overflow-x-auto">
        <div className="min-w-[860px]">
          <div className="grid grid-cols-[90px_1fr] bg-[#1d232a] h-[36px] items-center text-[12px] text-[#8a8f99] font-semibold">
            <span className="pl-4 uppercase">Tábla</span>
            <div className="relative h-full">
              {hours.map((h) => <span key={h.getTime()} className="absolute top-1/2 -translate-y-1/2" style={{ left: `${pct(h)}%` }}>{hhmm(h)}</span>)}
            </div>
          </div>
          {boards.map((b) => (
            <div key={b.id} className="grid grid-cols-[90px_1fr] h-[52px] border-t border-[#31383D]">
              <span className="pl-4 self-center text-[13px] font-semibold text-white">{b.order}. tábla</span>
              <div className="relative">
                {hours.map((h) => <span key={h.getTime()} className="absolute top-0 bottom-0 w-px bg-[#31383D]/60" style={{ left: `${pct(h)}%` }} />)}
                {b.state === 'maintenance' && (
                  <div className="absolute top-2 bottom-2 left-1 right-1 rounded-lg border px-2 flex items-center text-[11px] font-semibold text-white"
                    style={{ background: '#d9a51422', borderColor: '#d9a51488' }}>Karbantartás</div>
                )}
                {dayList.filter((x) => x.board === b.order).map((x) => {
                  const c = x.state === 'pending' ? '#d9a514' : KIND_COLOR[x.kind];
                  const l = pct(x.start);
                  const r = pct(x.end);
                  return (
                    <button key={x.id} type="button" onClick={() => setOpenId(x.id)} title={`${hhmm(x.start)}–${hhmm(x.end)}`}
                      className="absolute top-2 bottom-2 rounded-lg border px-2 text-left text-[11px] font-semibold text-white truncate hover:brightness-125"
                      style={{ left: `calc(${l}% + 2px)`, width: `calc(${Math.max(2, r - l)}% - 4px)`, background: `${c}22`, borderColor: `${c}99` }}>
                      {bookingTitle(x, who)}{x.state === 'pending' ? ' · Függőben' : x.kind === 'cp' ? ' · CP' : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {boards.length === 0 && <Empty>Még nincs tábla.</Empty>}
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4 text-[11px] text-[#8a8f99] flex-wrap">
        <span>Jelmagyarázat:</span>
        {[['#31383D', 'Elérhető időpont'], ['#00AFF2', 'CP rangsorolt'], ['#2bc48a', 'Baráti / sima'], ['#d9a514', 'Függőben'], ['#a855f7', 'Esemény']].map(([c, t]) => (
          <span key={t} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />{t}</span>
        ))}
      </div>
      {open && <BookingDrawer b={open} boards={boards} club={club} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/* ---------- Foglalás statisztika (OP_04) ---------- */
function StatsTab({ club, boards, bookings, range }: { club: ClubInfo; boards: ClubBoard[]; bookings: ClubBooking[]; range: 'day' | 'week' | 'month' }) {
  const days = range === 'day' ? 1 : range === 'week' ? 7 : 30;
  const now = Date.now();
  const cur = bookings.filter((b) => b.state !== 'cancelled' && b.start.getTime() <= now && b.start.getTime() > now - days * 864e5);
  const prev = bookings.filter((b) => b.state !== 'cancelled' && b.start.getTime() <= now - days * 864e5 && b.start.getTime() > now - 2 * days * 864e5);
  const who = usePeople(cur.map((b) => b.createdBy));
  const rev = cur.reduce((s, b) => s + bookingPrice(b, boards), 0);
  const revPrev = prev.reduce((s, b) => s + bookingPrice(b, boards), 0);
  const hoursBooked = cur.reduce((s, b) => s + b.durationMin, 0) / 60;

  const openMinutes = useMemo(() => {
    let m = 0;
    for (let i = 0; i < days; i++) {
      const w = openWindow(club.hours, new Date(now - i * 864e5));
      if (w) m += (w.to.getTime() - w.from.getTime()) / 60000;
    }
    return m;
  }, [club.hours, days, now]);
  const util = openMinutes && boards.length ? Math.min(100, Math.round(((hoursBooked * 60) / (openMinutes * boards.length)) * 100)) : 0;

  const perBoard = boards.map((b) => ({ b, n: cur.filter((x) => x.board === b.order).length }));
  const maxB = Math.max(1, ...perBoard.map((x) => x.n));
  const players = Object.entries(cur.filter((b) => !b.guestName && b.createdBy).reduce<Record<string, number>>((a, b) => { a[b.createdBy] = (a[b.createdBy] ?? 0) + 1; return a; }, {}))
    .sort((a, b) => b[1] - a[1]).slice(0, 5);
  const byHour = Array.from({ length: 24 }, (_, h) => cur.filter((b) => b.start.getHours() === h).length);
  const firstH = byHour.findIndex((n) => n > 0);
  const lastH = 23 - [...byHour].reverse().findIndex((n) => n > 0);
  const hRange = firstH < 0 ? [] : Array.from({ length: lastH - firstH + 1 }, (_, i) => firstH + i);
  const maxH = Math.max(1, ...byHour);
  const d = prev.length ? Math.round(((cur.length - prev.length) / prev.length) * 100) : null;
  const periodWord = range === 'day' ? 'előző naphoz' : range === 'week' ? 'előző héthez' : 'előző hónaphoz';

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon="CalendarBlank" tone="cyan" label="Összes foglalás" value={fmtInt(cur.length)} delta={d === null ? null : { text: `${Math.abs(d)}% az ${periodWord} képest`, up: d >= 0 }} />
        <StatCard icon="Clock" tone="purple" label="Foglalt órák" value={`${fmtInt(hoursBooked)} óra`} valueColor="#a855f7" />
        <StatCard icon="Coins" tone="green" label="Bevétel" value={`${fmtInt(rev)} Ft`} valueColor="#2bc48a"
          delta={revPrev ? { text: `${Math.abs(Math.round(((rev - revPrev) / revPrev) * 100))}%`, up: rev >= revPrev } : null} />
        <StatCard icon="TrendUp" tone="gold" label="Kihasználtság" value={`${util}%`} valueColor="#d9a514" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 mt-6 items-start">
        <Card className="!p-5">
          <SectionTitle>Táblánként</SectionTitle>
          {perBoard.map(({ b, n }) => (
            <div key={b.id} className="grid grid-cols-[110px_1fr_90px] items-center gap-3 py-1.5">
              <span className="text-[13px] text-white truncate">{b.name}</span>
              <span className="h-2 rounded-full bg-[#31383D] overflow-hidden"><span className="block h-full rounded-full bg-[#00AFF2]" style={{ width: `${(n / maxB) * 100}%` }} /></span>
              <span className="text-[12px] text-[#8a8f99] text-right">{n} foglalás</span>
            </div>
          ))}
          {boards.length === 0 && <Empty>Még nincs tábla.</Empty>}
        </Card>
        <Card className="!p-5">
          <SectionTitle right={<span className="text-[12px] text-[#8a8f99]">Legtöbbet foglalók</span>}>Top játékosok</SectionTitle>
          {players.length === 0 && <Empty>Nincs adat ebben az időszakban.</Empty>}
          {players.map(([uid, n], i) => (
            <div key={uid} className={`flex items-center gap-3 h-[46px] ${i < players.length - 1 ? 'border-b border-[#31383D]' : ''}`}>
              <span className={`w-4 text-[13px] font-semibold ${i === 0 ? 'text-[#d9a514]' : 'text-[#8a8f99]'}`}>{i + 1}</span>
              <Avatar src={who(uid).avatar} name={who(uid).name} size={28} />
              <span className="flex-1 text-[13px] text-white truncate">{who(uid).name}</span>
              <span className="text-[12px] text-[#8a8f99]">{n} foglalás</span>
            </div>
          ))}
        </Card>
      </div>

      <Card className="!p-5 mt-6">
        <SectionTitle>Foglalások óránként</SectionTitle>
        {hRange.length === 0 ? <Empty>Nincs adat ebben az időszakban.</Empty> : (
          <div className="flex items-end gap-3 h-[150px]">
            {hRange.map((h) => (
              <div key={h} className="flex-1 flex flex-col items-center justify-end h-full gap-2" title={`${byHour[h]} foglalás`}>
                <div className="w-full rounded-t-lg bg-gradient-to-b from-[#00AFF2] to-[#0b6f9a]" style={{ height: `${Math.max(4, (byHour[h] / maxH) * 120)}px` }} />
                <span className="text-[11px] text-[#8a8f99]">{h}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
